import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";

import api from "/imports/api/client";
import TermsCheckbox from "/imports/ui/components/shared/TermsCheckbox";
import useCheckoutPricing from "../../CheckoutView/useCheckoutPricing";
import CheckoutCreditsPanel from "../../CheckoutView/components/CheckoutCreditsPanel";
import { formatMoney, formatDuration } from "../dashboardHelpers";

function SettlePaymentDialog({ open, booking, slug, currency, onClose, onPaid }) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // The residual path redirects to the gateway; hitting back restores this page
  // from the bfcache with submitting still true, freezing the button. pageshow
  // fires on that restore — clear it so the dialog is usable again.
  useEffect(() => {
    const onPageShow = () => setSubmitting(false);
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // One dialog instance serves every unpaid booking in the list, so consent must
  // be re-taken each time it opens rather than inherited from the last booking.
  //
  // submitting is reset here too. The fully-covered path closes the dialog from
  // onPaid without clearing it, and this component stays mounted — so the next
  // booking opened would show a dead "Processing…" button with the close button
  // and backdrop both disabled, leaving a reload as the only way out.
  useEffect(() => {
    if (open) {
      setTermsAccepted(false);
      setSubmitting(false);
    }
  }, [open]);

  // Shape a checkout "item" from the existing booking so the shared pricing hook
  // can load this client's redeemable packages/vouchers/membership for it.
  // pricingId + duration scope the packages to this session's length — a pack
  // sold for 30 minutes can't pay for an hour of the same service.
  const primaryItem = booking
    ? {
        serviceId: booking.serviceId,
        locationId: booking.locationId,
        price: booking.price,
        date: booking.date,
        time: booking.time,
        pricingId: booking.pricingId || null,
        duration: booking.duration
      }
    : null;
  const pricing = useCheckoutPricing({ slug, primaryItem, items: primaryItem ? [primaryItem] : [] });

  const creditsPanelProps = {
    isAuthenticated: true,
    packagesLoading: pricing.packagesLoading,
    vouchersLoading: pricing.vouchersLoading,
    packages: pricing.packages,
    vouchers: pricing.vouchers,
    membership: pricing.membership,
    membershipApplies: pricing.membershipApplies,
    membershipSaving: pricing.membershipSaving,
    isPackageApplied: pricing.isPackageApplied,
    selectedPackageId: pricing.selectedPackageId,
    setSelectedPackageId: pricing.setSelectedPackageId,
    selectedVoucherId: pricing.selectedVoucherId,
    setSelectedVoucherId: pricing.setSelectedVoucherId,
    primaryAfterPackage: pricing.primaryAfterPackage,
    displayCurrency: currency
  };

  const handleConfirm = async () => {
    if (!booking || !termsAccepted) return;
    setSubmitting(true);
    try {
      const res = await api.settleBooking(slug, booking._id, {
        paidPlanId: pricing.selectedPackageId || null,
        voucherId: pricing.selectedVoucherId || null
      });

      // Credits fully covered it — nothing to charge online. Land on the same
      // confirmation the widget's own checkout shows, so a booking paid with a
      // package ends on a receipt naming the package rather than a toast.
      //
      // The row is still patched to Paid on the way out: the confirmation is a
      // separate route, and coming back to the dashboard must not offer Pay Now
      // for a booking that is already settled.
      if (res.isPaid) {
        onPaid({ bookingId: booking._id, invoiceId: res.invoiceId });
        // The confirmation route sits outside /:slug, so it reads the club from here.
        sessionStorage.setItem("widget_slug", slug);
        navigate(`/checkout/confirmation/${res.invoiceId}`);
        return;
      }

      const { chargeId } = await api.getInvoicePayLink(slug, res.invoiceId);
      if (!chargeId) {
        enqueueSnackbar("No payment link available for this booking", { variant: "error" });
        setSubmitting(false);
        return;
      }
      // Soft SPA navigation (same as CheckoutView) — a hard window.location reload
      // would white-flash the whole app and reload it again on back.
      navigate(`/checkout/telr/${chargeId}?clubId=${slug}`);
    } catch (err) {
      setSubmitting(false);
      enqueueSnackbar(err.message || "Failed to complete payment", { variant: "error" });
    }
  };

  const durationLabel = booking ? formatDuration(booking.duration) : "";
  const isFullyCovered = pricing.total <= 0;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 2, pb: 0.5 }}>
        <Typography sx={{ fontSize: "1.125rem", fontWeight: 700 }}>Pay for your booking</Typography>
        <IconButton size="small" onClick={onClose} disabled={submitting} sx={{ mr: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* DialogContent's own horizontal padding is zeroed: the shared credits
          panel already carries its own inset, so stacking both over-pads (and a
          negative pull overflows the dialog). The header matches the panel's inset. */}
      <DialogContent sx={{ pt: 1, px: 0 }}>
        {booking && (
          <Box sx={{ mb: 1.5, px: 2 }}>
            <Typography variant="subtitle2">{booking.serviceName}</Typography>
            <Typography variant="body2" color="textSecondary">
              {booking.date} at {booking.time}
              {durationLabel && ` • ${durationLabel}`}
              {booking.location && ` — ${booking.location}`}
            </Typography>
          </Box>
        )}

        <CheckoutCreditsPanel embedded {...creditsPanelProps} />
      </DialogContent>

      <Divider />

      {/* disableSpacing: DialogActions otherwise indents every sibling after the
          first by 8px, which knocks both the checkbox and the button off the
          Total row's left edge. The column gap already handles the spacing. */}
      <DialogActions disableSpacing sx={{ px: 2, py: 2, flexDirection: "column", alignItems: "stretch", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <Typography variant="body2" color="textSecondary">
            Total
          </Typography>
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 800 }}>{formatMoney(pricing.total, currency)}</Typography>
        </Box>
        {/* Pinned beside the button rather than in the scroll area: a long credits
            list would otherwise push the consent gate out of view above the CTA. */}
        <TermsCheckbox checked={termsAccepted} onChange={setTermsAccepted} disabled={submitting} sx={{ ml: -0.75 }} />
        <Button
          variant="contained"
          fullWidth
          onClick={handleConfirm}
          disabled={submitting || !termsAccepted}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ py: 1.1, textTransform: "none", fontWeight: 700, color: "common.white", boxShadow: "none" }}
        >
          {submitting ? "Processing…" : isFullyCovered ? "Confirm" : "Pay now"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SettlePaymentDialog;
